/**
 * Archived Child Card Component
 * Displays archived children with re-establish and permanent delete options
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Trash2, Phone, Mail, Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";

export const DeletedChildCard = ({
  child,
  onRestore,
  onPermanentDelete,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
  canEdit = true
}) => {
  return (
    <Card className="bg-red-50/80 backdrop-blur-sm border-2 border-red-200">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {selectMode && canEdit && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(child.id)}
                className="mt-1"
                data-testid={`select-archived-${child.id}`}
              />
            )}
            <div className="flex-1">
              <CardTitle className="text-xl text-red-900">{child.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline" className="border-red-300 text-red-700">
                  Archived
                </Badge>
                {child.category && (
                  <Badge variant="outline" className="text-xs">
                    Was: {child.category.replace('_', ' ')}
                  </Badge>
                )}
                {child.deactivatedAt && (
                  <span className="text-xs text-red-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(parseISO(child.deactivatedAt), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button
                onClick={() => onRestore(child)}
                variant="outline"
                size="sm"
                className="border-green-300 text-green-700 hover:bg-green-50"
                title="Re-establish Child"
                data-testid={`reestablish-child-${child.id}`}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Re-establish</span>
              </Button>
              <Button
                onClick={() => onPermanentDelete(child)}
                variant="outline"
                size="sm"
                className="border-red-500 text-red-700 hover:bg-red-100"
                title="Permanently Delete"
                data-testid={`permanent-delete-${child.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {child.pickupPersons && child.pickupPersons.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-red-800">Authorized Pickup:</p>
            {child.pickupPersons.map((person, index) => (
              <div key={index} className="flex flex-col text-sm text-red-700 bg-red-100/50 p-2 rounded">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{person.name}</span>
                  {person.phone && (
                    <span className="flex items-center gap-1 opacity-70">
                      <Phone className="w-3 h-3" />
                      {person.phone}
                    </span>
                  )}
                  {person.email && (
                    <span className="flex items-center gap-1 opacity-70">
                      <Mail className="w-3 h-3" />
                      {person.email}
                    </span>
                  )}
                </div>
                {person.restrictions && (
                  <span className="text-xs text-red-600 mt-1">
                    Note: {person.restrictions}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No pickup persons added</p>
        )}
      </CardContent>
    </Card>
  );
};

export default DeletedChildCard;
